/**
 * Tour P5 (2026-07-07) — derivación ÚNICA de la tripulación a partir de los
 * valores del form de Operarios y vehículos (o de su borrador persistido en
 * sessionStorage — mismo shape).
 *
 * Fuente de verdad de los vehículos seleccionados = `vehicleTable`
 * (Record vehicleId → { vehicleID, driverID }): es lo que puebla y limpia el
 * handler de selección de `OperatorsSelectionTable` (al marcar auto-asigna
 * como conductor al primer operario; al desmarcar borra la entrada).
 * `vehiclesTable` NO es fiable — puede contener objetos vacíos `{}` (visto
 * en borradores reales), que inflan el conteo o vacían los ids.
 *
 * La usan el Iniciar del wizard RV (validación FR-3.4 + payload
 * `vehicleAssignments`) y el recálculo en vivo (`onCrewChange`) — una sola
 * lógica, imposible que diverjan.
 */

export type CrewVehicleAssignment = {
  vehicleId: string;
  driverId: string;
};

export type CrewSummary = {
  operators: number;
  vehicles: number;
  vehiclesWithoutDriver: number;
  /** Ids de los operarios seleccionados (para `userIDs`). */
  operatorIds: string[];
  /** Parejas vehículo→conductor COMPLETAS (para `vehicleAssignments`). */
  assignments: CrewVehicleAssignment[];
};

/**
 * Catálogo cargado (operarios y vehículos que la pantalla PUEDE enseñar).
 *
 * Cinturón del incidente 2026-08-08: si el borrador arrastra un id que ya no
 * existe en el catálogo (operario dado de baja, vehículo de otra concesión,
 * resto de un intento anterior), no debe viajar en el payload.
 *
 * 🔴 **Fail-open a propósito**: si el catálogo llega vacío —arranque en frío,
 * tablet sin cobertura— NO se filtra nada. Un operario de campo no puede
 * quedarse sin poder crear el recorrido porque su catálogo no cargó.
 */
export type CrewCatalog = {
  operatorIds?: string[];
  vehicleIds?: string[];
};

export function deriveCrew(
  values: {
    operatorsTable?: unknown[];
    vehicleTable?: unknown;
  },
  catalog?: CrewCatalog,
): CrewSummary {
  const knownOperatorIds = catalog?.operatorIds?.length
    ? new Set(catalog.operatorIds.map(String))
    : null;
  const knownVehicleIds = catalog?.vehicleIds?.length
    ? new Set(catalog.vehicleIds.map(String))
    : null;

  const seenOperatorIds = new Set<string>();
  const operators = ((values?.operatorsTable as any[]) || []).filter(
    (op: any) => {
      const id = op && (op.id || op.userId);
      if (!id) return false;
      // Un mismo operario dos veces en el borrador contaría doble en las
      // frases FR-3.4 y viajaría repetido en `userIDs`.
      if (seenOperatorIds.has(String(id))) return false;
      if (knownOperatorIds && !knownOperatorIds.has(String(id))) return false;
      seenOperatorIds.add(String(id));
      return true;
    },
  );
  const operatorIds = operators.map((op: any) => op.id || op.userId);

  const vehicleTable = (values?.vehicleTable as Record<string, any>) || {};
  // Puede llegar como Record (clave = vehicleId) o como array según el
  // contexto (mismo doble-shape que maneja utils/formValidation.ts).
  const entries: Array<[string, any]> = Array.isArray(vehicleTable)
    ? vehicleTable
        .map((v: any, i: number): [string, any] => [
          String(v?.vehicleID || v?.id || i),
          v,
        ])
        .filter(([, v]) => v)
    : Object.entries(vehicleTable).filter(([, v]) => v);

  // BUG borrador RV (2026-07-12): el fallback `|| key` contaba como
  // seleccionadas las entradas fantasma `{}` (materializadas por la antigua
  // caché global de conductores) — "vehículo sin conductor" falso en la
  // validación. El handler de selección real SIEMPRE setea vehicleID, así
  // que una entrada sin identificador de vehículo es debris, no selección.
  const seenVehicleIds = new Set<string>();
  const selected = entries.filter(([key, v]) => {
    const vehicleId = v?.vehicleID || v?.id;
    if (!vehicleId) return false;
    // En forma Record la clave ya es única, pero `vehicleTable` también puede
    // llegar como array (mismo doble-shape que utils/formValidation.ts) y ahí
    // el mismo vehículo dos veces se convertiría en DOS `VehicleOccupant`.
    const dedupeKey = String(vehicleId || key);
    if (seenVehicleIds.has(dedupeKey)) return false;
    if (knownVehicleIds && !knownVehicleIds.has(dedupeKey)) return false;
    seenVehicleIds.add(dedupeKey);
    return true;
  });
  const assignments: CrewVehicleAssignment[] = selected
    .filter(([, v]) => v?.driverID || v?.driver)
    .map(([key, v]) => ({
      vehicleId: String(v?.vehicleID || v?.id || key),
      driverId: String(v?.driverID || v?.driver),
    }));

  return {
    operators: operators.length,
    vehicles: selected.length,
    vehiclesWithoutDriver: selected.length - assignments.length,
    operatorIds,
    assignments,
  };
}
